"use client";

import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { FlameIcon, flameKindForStreak } from "#/components/FlameIcon";
import { MedalIcon } from "#/components/MedalIcon";
import { Button } from "#/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "#/components/ui/popover";
import { Separator } from "#/components/ui/separator";
import { Skeleton } from "#/components/ui/skeleton";
import { isRegisteredPlayerId } from "#/lib/playerDisplayName";
import { playerPreviewQueryOptions } from "#/lib/queries";
import { cn } from "#/lib/utils";
import type { PlayerPreview } from "#/server/game/playerPreview";

function PreviewMedals({
  goldMedals,
  silverMedals,
  bronzeMedals,
}: {
  goldMedals: number;
  silverMedals: number;
  bronzeMedals: number;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="inline-flex items-center gap-1 tabular-nums">
        <MedalIcon tier="gold" size={16} />
        {goldMedals}
      </span>
      <span className="inline-flex items-center gap-1 tabular-nums">
        <MedalIcon tier="silver" size={16} />
        {silverMedals}
      </span>
      <span className="inline-flex items-center gap-1 tabular-nums">
        <MedalIcon tier="bronze" size={16} />
        {bronzeMedals}
      </span>
    </div>
  );
}

function PreviewStatRow({
  label,
  value,
  streak,
}: {
  label: string;
  value: number | string;
  streak?: boolean;
}) {
  const kind = streak && typeof value === "number" ? flameKindForStreak(value) : null;
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="inline-flex items-center gap-1 font-medium tabular-nums text-foreground">
        {kind ? <FlameIcon kind={kind} size={14} /> : null}
        {value}
      </span>
    </div>
  );
}

function PlayerPreviewLoaded({ preview }: { preview: PlayerPreview }) {
  return (
    <>
      <PopoverHeader>
        <PopoverTitle className="truncate">{preview.displayName}</PopoverTitle>
        <PopoverDescription className="sr-only">Player preview</PopoverDescription>
        <PreviewMedals
          goldMedals={preview.goldMedals}
          silverMedals={preview.silverMedals}
          bronzeMedals={preview.bronzeMedals}
        />
      </PopoverHeader>
      <div className="flex flex-col gap-1.5 px-0.5">
        <PreviewStatRow label="Games played" value={preview.gamesCompleted} />
        <PreviewStatRow label="Standard streak" value={preview.standardDailyStreak} streak />
        <PreviewStatRow label="Premium streak" value={preview.premiumDailyStreak} streak />
        <PreviewStatRow
          label="Achievements"
          value={`${preview.earnedAchievementCount}/${preview.totalAchievementCount}`}
        />
      </div>
      <Separator />
      <Button
        size="sm"
        className="w-full"
        render={<Link to="/profile/$name" params={{ name: preview.displayName }} preload={false} />}
        nativeButton={false}
      >
        View profile
      </Button>
    </>
  );
}

function PlayerPreviewPending() {
  return (
    <>
      <PopoverHeader>
        <Skeleton className="h-5 w-28" />
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-10" />
        </div>
      </PopoverHeader>
      <div className="flex flex-col gap-1.5 px-0.5">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
      <Separator />
      <Skeleton className="h-7 w-full" />
    </>
  );
}

function PlayerPreviewBody({ playerId, open }: { playerId: string; open: boolean }) {
  const { data, isPending, isError } = useQuery({
    ...playerPreviewQueryOptions(playerId),
    enabled: open,
  });

  if (isPending) return <PlayerPreviewPending />;
  if (isError || !data) {
    return <p className="px-0.5 text-xs text-muted-foreground">Could not load player.</p>;
  }
  return <PlayerPreviewLoaded preview={data} />;
}

export function PlayerPreviewName({
  playerId,
  displayName,
  className,
  onOpenChange,
  closeSignal,
}: {
  playerId: string;
  displayName: string;
  className?: string;
  onOpenChange?: (open: boolean) => void;
  closeSignal?: number;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (closeSignal === undefined) return;
    setOpen(false);
  }, [closeSignal]);

  if (!isRegisteredPlayerId(playerId)) {
    return <span className={className}>{displayName}</span>;
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
      }}
    >
      <PopoverTrigger
        className={cn(
          "cursor-pointer text-foreground hover:underline outline-none focus-visible:underline",
          className,
        )}
        draggable={false}
      >
        {displayName}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        positionMethod="fixed"
        className="w-56 gap-2 p-2.5"
      >
        <PlayerPreviewBody playerId={playerId} open={open} />
      </PopoverContent>
    </Popover>
  );
}
